import { ShieldCheck } from "lucide-react";

const KITCHEN = [
  ["Dinner Plate", "500"], ["Quarter Plate", "300"], ["Soup Bowl", "300"], ["Coffee Mug", "400"],
  ["Tea Cup & Saucer", "500"], ["Water Glass", "300"], ["Whiskey Glass", "600"], ["Wine Glass", "500"],
  ["Serving Bowl", "1,000"], ["Serving Tray", "1,500"], ["Cutlery (per piece)", "250"],
  ["Kitchen Knife", "500"], ["Electric Kettle", "2,000"],
];
const LIVING = [
  ["Decorative Cushion", "800"], ["Cushion Cover", "400"], ["Coffee Table", "8,000"], ["Side Table", "5,000"],
  ["Decorative Showpiece", "1,500 – 10,000"], ["TV Remote", "2,000"], ["Smart TV", "Actual Replacement Cost"],
  ["Bluetooth Speaker", "Actual Replacement Cost"], ["Microwave", "Actual Replacement Cost"],
];
const OUTDOOR = [
  ["Pool Towel", "750"], ["Outdoor Chair", "3,000"], ["Outdoor Table", "8,000"], ["Sun Lounger", "10,000"],
  ["Garden Umbrella", "7,500"], ["BBQ Equipment", "Actual Repair/Replacement Cost"],
];
const PENALTIES = [
  ["Smoking Inside Villa", "10,000"], ["Unauthorized Party/Event/Substance", "25,000"],
  ["Excessive Cleaning Requirement", "5,000 – 15,000"], ["Damage to Walls/Artwork", "Actual Repair Cost"],
  ["Excessive Garbage Disposal", "2,000"], ["Pet Related Damage", "Actual Repair Cost"],
  ["Loss of Any Villa Asset", "Replacement Cost + Handling Charges"],
];

function ChargeTable({ title, rows }) {
  return (
    <div className="mt-6">
      <h3 className="font-heading text-xl">{title}</h3>
      <div className="mt-3 border border-border rounded-lg overflow-hidden">
        {rows.map(([item, charge], i) => (
          <div key={item} className={`flex justify-between px-4 py-2.5 text-sm ${i % 2 ? "bg-secondary/40" : "bg-card"}`}>
            <span>{item}</span>
            <span className="font-medium text-accent">{charge.includes("Cost") ? charge : `\u20B9${charge}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Terms() {
  return (
    <div data-testid="terms-page" className="bg-secondary/30">
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
          <ShieldCheck className="h-9 w-9 text-accent mb-4" strokeWidth={1.5} />
          <h1 className="font-heading text-4xl sm:text-5xl font-light">Terms &amp; Conditions</h1>
          <p className="text-primary-foreground/75 mt-3">Apukaa Villa, Lonavala — Guest Inventory, Security Deposit &amp; Property Usage Policy</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-8">
        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Welcome to Apukaa Villa</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Apukaa Villa is a privately owned property and not a commercial hotel. We take great pride in
            maintaining the villa, its furnishings and amenities. By confirming your booking and occupying the
            villa, you agree to comply with the following terms and conditions.
          </p>
        </section>

        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Security Deposit</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            A refundable security deposit of <strong className="text-foreground">₹10,000</strong> shall be collected prior to check-in. It may
            be adjusted against breakage/damage to villa property or inventory, missing items, excessive
            cleaning, unauthorized events, smoking inside the villa, delayed checkout, or any violation resulting
            in financial loss. The balance, if any, is refunded within 48 hours after checkout inspection.
          </p>
        </section>

        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Check-in &amp; Check-out</h2>
          <ul className="text-muted-foreground mt-3 space-y-1">
            <li>Check-in Time: <strong className="text-foreground">02:00 PM</strong> onwards</li>
            <li>Check-out Time: <strong className="text-foreground">11:00 AM</strong> sharp</li>
          </ul>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Guests staying beyond the checkout time will be chargeable at the Owner's discretion. Guests
            continuing to occupy the villa for more than two (2) hours beyond the scheduled checkout shall be
            liable to pay one full day's applicable villa tariff.
          </p>
        </section>

        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Cancellation &amp; Refund Policy</h2>
          <ul className="text-muted-foreground mt-3 space-y-2 leading-relaxed">
            <li>• <strong className="text-foreground">Owner/Admin cancellation:</strong> booking may be cancelled due to any issue with a <strong className="text-foreground">100% refund</strong>.</li>
            <li>• <strong className="text-foreground">Customer cancellation 10+ days before check-in:</strong> 100% refund.</li>
            <li>• <strong className="text-foreground">Customer cancellation within 10 days of check-in:</strong> 10% cancellation fee; the remaining amount is refunded to the original payment account.</li>
          </ul>
        </section>

        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Inventory Breakage &amp; Damage Charges</h2>
          <ChargeTable title="Kitchen & Dining" rows={KITCHEN} />
          <ChargeTable title="Living Area" rows={LIVING} />
          <ChargeTable title="Pool & Outdoor Area" rows={OUTDOOR} />
          <ChargeTable title="Additional Charges & Penalties" rows={PENALTIES} />
          <p className="text-xs text-muted-foreground mt-6">
            The above charges are indicative and represent minimum recoverable amounts. Where actual repair,
            restoration or replacement cost exceeds the amount specified, Apukaa Villa reserves the right to
            recover the actual cost incurred, supported by invoices, quotations or market replacement value.
          </p>
        </section>

        <section className="bg-card border border-border rounded-lg p-8">
          <h2 className="font-heading text-2xl font-light">Guest Declaration</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            I/We acknowledge that Apukaa Villa is a private property and agree to abide by the villa rules,
            inventory policy, security deposit terms and checkout requirements. I/We accept liability for any
            damage, breakage, loss, misuse or delayed checkout occurring during our stay.
          </p>
        </section>
      </div>
    </div>
  );
}
